// src/encounter/lib/to-ap-search-item.ts
// Map each A/P category's backend search rows → the DS AssessmentPlanSearchPanel `ApSearchItem`
// shape ({ id, code?, label, system?, item }). `item` carries the ORIGINAL row so a drag/activate
// round-trips it back to the host: a diagnosis item's `item` is a DS CodeSearchResult (fed straight
// to onAddDx); order items keep their native row (labs/imaging/procedure/referral are already
// CodeSearchResult, medications are MedicationOption, vaccines are a DDC VaccineValueSet).
import type { ApSearchItem } from '@scripsteam/scrips-signal-ds';
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type { ConditionCode, ImmunizationCoding } from '@/types/patient-chart-api';
import type { MedicationOption } from './to-medication-result';
import type { VaccineValueSet } from '@/shared/services/use-vaccination-search';
import { toCodeSearchResults } from './to-code-search-result';
import { searchRowView, typeLabel } from './lab-order-v3';
import { toLabOrderSearchPick, type LabOrderSearchResult } from '../services/use-lab-order-search';

/** labs / imaging / procedure / referral — already the DS CodeSearchResult shape. */
export function codeResultsToApItems(results: CodeSearchResult[] | null | undefined): ApSearchItem[] {
  return (results ?? []).map((r) => ({
    id: r.id,
    code: r.code,
    label: r.display,
    system: r.system,
    item: r,
  }));
}

/** Diagnoses — ConditionCode rows go through the existing ICD-10 mapper first (so `item` is the
 *  CodeSearchResult onAddDx expects), then to ApSearchItem. */
export function conditionCodesToApItems(codes: ConditionCode[] | null | undefined): ApSearchItem[] {
  return codeResultsToApItems(toCodeSearchResults(codes));
}

/** Medications — MedicationOption { id, name, code, system, … }. `item` stays the full option so the
 *  host's medication flow (dose dialog) has route/units/strength. */
export function medicationOptionsToApItems(opts: MedicationOption[] | null | undefined): ApSearchItem[] {
  return (opts ?? []).map((m) => ({
    id: m.id,
    code: m.code,
    label: m.name,
    system: m.system,
    item: m,
  }));
}

/** Vaccines — ImmunizationCoding (mixed casing on the wire).
 *  WRAPPED-vs-FLAT (live probe 2026-07-01): `/api/Master/VaccinationCodesList` ships a WRAPPED row —
 *  `{ Id, code: { code, display, System, Definition } }` (coding nested under `code`, PascalCase
 *  `System`) — even though `ImmunizationCoding` is typed flat. The old mapper read `c.code` at the top
 *  level, so `c.code` was an OBJECT and `(c.code ?? '').trim()` threw a TypeError → the A/P vaccine tab
 *  blanked to an error page. Unwrap the nested coding (handling both shapes), read PascalCase `System`,
 *  and emit a NORMALISED flat coding as `item` so the seeded VaccineAddDialog gets a clean coding. */
export function immunizationsToApItems(codings: ImmunizationCoding[] | null | undefined): ApSearchItem[] {
  return (codings ?? []).flatMap((row, i): ApSearchItem[] => {
    const r = row as {
      code?: unknown; display?: unknown; description?: unknown; Definition?: unknown;
      system?: string | null; System?: string | null;
    };
    // Wrapped ({code:{...}}) → use the nested coding; flat → use the row itself.
    const c = r.code && typeof r.code === 'object' ? (r.code as typeof r) : r;
    const code = (typeof c.code === 'string' ? c.code : '').trim();
    const label = String(c.display ?? c.description ?? c.Definition ?? code ?? '').trim();
    const system = c.system ?? c.System ?? undefined;
    if (!label && !code) return [];
    const clean: ImmunizationCoding = {
      code: code || null,
      display: typeof c.display === 'string' ? c.display : null,
      system: system ?? null,
      Definition: typeof c.Definition === 'string' ? c.Definition : null,
    };
    return [{
      id: code || `${label}-${i}`,
      code: code || undefined,
      label: label || code,
      system,
      item: clean,
    }];
  });
}

/**
 * A/P VACCINE ROWS — the DDC product value-set (PROD-1819).
 *
 * The A/P rail orders a PRODUCT, so it must search the same DDC catalogue Inventory is keyed by:
 * `SearchValueSets2?SearchFor=Vaccination` → `{ valueCoding, route[], unitOfMeasurement }`. This
 * replaces `immunizationsToApItems` on the ordering surface, which fed the rail from
 * `VaccinationCodesList` — the CVX *concept* list.
 *
 * WHY THAT MATTERED: a CVX code (`15` Influenza) can never resolve a lot, because
 * `Inventory/Items/ByCode/{code}/Lots` is keyed by DDC product code. Verified on dev —
 * `ByCode/15/Lots` → `[]`, `ByCode/0036-103402-0511/Lots` → the VAXIGRIP lot. So every vaccine a
 * clinician could add had an empty LotPicker and the PROD-1221 deduction could never fire.
 *
 * Flutter agrees: its A/P rail uses `FetchDDCVaccinationsUseCase` → this endpoint
 * (assessment_plan_search_list_widget.dart:1343), and reserves the CVX list for the patient chart's
 * immunisation HISTORY, gated on `resourceBasedIntegration.vaccinationHistorySystem == 'IHL'`
 * (patient_chart/.../ui_data.dart:596-602). CVX is the concept; DDC is the product with stock.
 *
 * `item` carries the WHOLE value-set, not a narrowed coding, so `route[]` and `unitOfMeasurement`
 * survive the trip to the dialog instead of being rebuilt as empty.
 */
export function vaccineValueSetsToApItems(rows: VaccineValueSet[] | null | undefined): ApSearchItem[] {
  return (rows ?? []).flatMap((row, i): ApSearchItem[] => {
    const coding = row?.valueCoding;
    const code = (coding?.code ?? '').trim();
    const label = (coding?.display ?? '').trim();
    if (!code && !label) return [];
    return [{
      id: code || `${label}-${i}`,
      code: code || undefined,
      label: label || code,
      ...(coding?.system?.trim() ? { system: coding.system.trim() } : {}),
      item: row,
    }];
  });
}

/**
 * Labs on the order search (PROD-2931): the clinic's own tests and panels first, then the Scrips
 * standard list, as the Add lab popup lists them (board 9). Each row shows the clinic code, the name
 * and the type ("Single test", "Panel", "Clinic panel"); a standard-list row says so. No prices: the
 * route returns none. `item` is the LabOrderSearchPick the popup opens by id.
 */
export function labOrderItemsToApItems(result: LabOrderSearchResult | null | undefined): ApSearchItem[] {
  const items = result?.items ?? [];
  const ordered = [...items.filter((i) => !i.fromStandardList), ...items.filter((i) => i.fromStandardList)];
  return ordered.map((i) => {
    const pick = toLabOrderSearchPick(i);
    const label = [i.name, typeLabel(i.type), i.fromStandardList ? 'Standard list' : null].filter(Boolean).join(' · ');
    const subLine = searchRowView(i).subLine;
    return {
      id: i.id,
      ...(pick.code ? { code: pick.code } : {}),
      label,
      ...(subLine ? { title: subLine } : {}),
      item: pick,
    };
  });
}
